import ThemedSelect from "../components/shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Alert, Button, Form, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ReportMasthead } from "../components/reports/ReportDocument.jsx";
import RulesHelp from "../components/shared/RulesHelp.jsx";
import { getFileLifeReport, listEntityRows, listUsers } from "../services/apiClient.js";
import { formatDate, todayInput, monthStart } from "../utils/date.js";

// File Life report — 1:1 port of FileLifeTarget.aspx (same columns, same
// footer average) so 1.0 and 2.0 can run the SAME report on the same data and
// the numbers can be compared eyeball-to-eyeball. File Life = business days
// Accepted → Completed minus non-GPS-fault hold days (sla.md "File Life");
// > 25 days = over target (red). The On Hold column is the per-reason day
// breakdown (legacy GetHTMLDisplayDaysOnHold).

const defaultFrom = () => monthStart(0); // 1st of this NZ month (legacy default)

// Show Columns (Robert 2026-08-01, the same control the feedback reports carry):
// thirteen columns do not fit any sheet of paper, and which ones matter depends on
// who the copy is for. The choice is remembered per browser. File Life itself is
// deliberately last in the list and on by default — it is the column the report is
// named after, and it was the one falling off the edge.
const COLUMN_DEFS = [
  ["gps", "GPS #"], ["division", "Division"], ["insurer", "Insurer"],
  ["claim", "Insurance Claim #"], ["assessor_co", "Assessor Company"],
  ["assessor_ref", "Assessor Ref"], ["client", "Client"], ["address", "Address"],
  ["status", "Status"], ["accepted", "Accepted"], ["hold", "On Hold"],
  ["completed", "Completed"], ["life", "File Life"],
];
const COLS_KEY = "fileLifeCols:v1";

function FileLifeReportPage() {
  const [report, setReport] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [jobTypes, setJobTypes] = useState([]);
  const [insurers, setInsurers] = useState([]);
  const [assessorCos, setAssessorCos] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({
    from: defaultFrom(), to: todayInput(),
    basis: "completed", job_number: "",
    division_id: "", project_coordinator_id: "", job_type_id: "", insurer_id: "", assessor_company_id: "",
  });
  const [error, setError] = useState("");
  const loadCols = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_KEY));
      if (Array.isArray(saved)) return new Set(saved);
    } catch { /* fall through */ }
    return new Set(COLUMN_DEFS.map(([k]) => k));
  };
  const [cols, setCols] = useState(loadCols);
  const col = (k) => cols.has(k);
  const toggleCol = (k) => {
    setCols((c) => {
      const next = new Set(c);
      if (next.has(k)) next.delete(k); else next.add(k);
      localStorage.setItem(COLS_KEY, JSON.stringify([...next]));
      return next;
    });
  };
  const navigate = useNavigate();

  // What this copy covers, for the masthead: insurer first, then assessor company,
  // else everyone.
  const nameOf = (list, id) =>
    (id && (list || []).find((r) => String(r.id) === String(id))?.name) || null;
  const scopeLabel =
    nameOf(insurers, f.insurer_id) || nameOf(assessorCos, f.assessor_company_id) || "All companies";
  async function load(params = f) {
    setError("");
    try {
      setReport(await getFileLifeReport(params));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to load the File Life report.");
    }
  }

  useEffect(() => {
    listEntityRows("divisions").then(setDivisions).catch(() => {});
    listEntityRows("job-types").then(setJobTypes).catch(() => {});
    listEntityRows("insurer-companies").then(setInsurers).catch(() => {});
    listEntityRows("assessor-companies").then(setAssessorCos).catch(() => {});
    listUsers({ pco: true, activeOnly: true }).then(setUsers).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (name, value) => setF((cur) => ({ ...cur, [name]: value }));
  const sel = (label, name, options, labelField = "name") => (
    <div>
      <Form.Label className="mb-0 small text-muted">{label}</Form.Label>
      <ThemedSelect size="sm" style={{ width: 170 }} value={f[name]} onChange={(e) => set(name, e.target.value)}>
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o[labelField]}</option>
        ))}
      </ThemedSelect>
    </div>
  );

  return (
    <section className="page-panel">
      {/* App chrome: the document carries its own masthead (report-document.css). */}
      <div className="page-heading no-print">
        <h2>File Life <RulesHelp topic="fileLife" /></h2>
      </div>

      <div className="report-controls d-flex flex-wrap align-items-end column-gap-2 row-gap-2 pb-2 border-bottom mb-2 no-print">
        <div>
          <Form.Label className="mb-0 small text-muted">From</Form.Label>
          <Form.Control size="sm" type="date" style={{ width: 150 }} value={f.from} onChange={(e) => set("from", e.target.value)} />
        </div>
        <div>
          <Form.Label className="mb-0 small text-muted">To</Form.Label>
          <Form.Control size="sm" type="date" style={{ width: 150 }} value={f.to} onChange={(e) => set("to", e.target.value)} />
        </div>
        <div className="d-flex flex-column">
          <Form.Label className="mb-0 small text-muted">Date applies to</Form.Label>
          <div className="d-flex column-gap-3 pt-1">
            {[["completed", "Completed"], ["accepted", "Accepted"]].map(([v, label]) => (
              <Form.Check key={v} inline type="radio" id={`fl-basis-${v}`} name="fl-basis" label={label}
                checked={f.basis === v} onChange={() => set("basis", v)} className="me-0" />
            ))}
          </div>
        </div>
        {sel("Division", "division_id", divisions)}
        {sel("Job Type", "job_type_id", jobTypes)}
        {sel("Insurer", "insurer_id", insurers)}
        {sel("Assessor Co", "assessor_company_id", assessorCos)}
        {sel("Project Co-Ordinator", "project_coordinator_id", users, "full_name")}
        <div>
          <Form.Label className="mb-0 small text-muted">Job #</Form.Label>
          <Form.Control size="sm" type="number" style={{ width: 110 }} value={f.job_number}
            placeholder="just one" data-hint="Show a single job regardless of the other filters"
            onChange={(e) => set("job_number", e.target.value)} />
        </div>
        <Button size="sm" onClick={() => load()}>Search</Button>
      </div>

      <div className="d-flex align-items-center column-gap-3 flex-wrap mb-2 no-print">
        <span className="small fw-semibold text-muted">Show columns:</span>
        {COLUMN_DEFS.map(([k, label]) => (
          <Form.Check
            key={k} inline type="checkbox" id={`fl-col-${k}`} label={label}
            className="small" checked={cols.has(k)} onChange={() => toggleCol(k)}
          />
        ))}
      </div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {!report ? (
        <p className="text-muted">Loading…</p>
      ) : (
        <div className="report-doc report-doc-wide border rounded">
          <ReportMasthead
            report="File Life"
            title={scopeLabel}
            subtitle={`Grayburn Property Services · ${f.job_number ? `job ${f.job_number}` : `${formatDate(f.from)} to ${formatDate(f.to)}`}`}
          />
          <div className="report-body">
          <div className="d-flex flex-wrap column-gap-4 pb-2 small text-muted">
            <span>{report.summary.jobs} job{report.summary.jobs === 1 ? "" : "s"}{report.summary.jobs >= report.cap ? ` (capped at ${report.cap})` : ""}</span>
            <span>with a file life: {report.summary.with_file_life}</span>
            <span className="fw-semibold">Avg: {report.summary.avg == null ? "—" : report.summary.avg}</span>
          </div>
          <div className="report-scroll">
            <Table striped bordered hover size="sm" className="mb-0 align-middle report-table" style={{ fontSize: "0.82rem" }}>
              <thead className="table-surface">
                <tr>
                  {col("gps") ? <th>GPS #</th> : null}
                  {col("division") ? <th>Division</th> : null}
                  {col("insurer") ? <th>Insurer</th> : null}
                  {col("claim") ? <th>Insurance Claim #</th> : null}
                  {col("assessor_co") ? <th>Assessor Company</th> : null}
                  {col("assessor_ref") ? <th>Assessor Ref</th> : null}
                  {col("client") ? <th>Client</th> : null}
                  {col("address") ? <th>Address</th> : null}
                  {col("status") ? <th>Status</th> : null}
                  {col("accepted") ? <th>Accepted</th> : null}
                  {col("hold") ? <th>On Hold</th> : null}
                  {col("completed") ? <th>Completed</th> : null}
                  {col("life") ? <th data-hint="Business days Accepted → Completed minus non-GPS-fault hold days · over 25 = red">File Life</th> : null}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.id}>
                    {col("gps") ? (
                      <td className="text-nowrap">
                        <span role="button" className="text-primary clickable" onClick={() => navigate(`/claims/${r.claim_number}`)}>
                          {r.claim_number}
                        </span>
                      </td>
                    ) : null}
                    {col("division") ? <td className="text-nowrap">{r.division_name || "-"}</td> : null}
                    {col("insurer") ? <td className="text-truncate" style={{ maxWidth: 130 }} data-hint={r.insurer_name || undefined}>{r.insurer_name || "-"}</td> : null}
                    {col("claim") ? <td className="text-nowrap">{r.insurance_claim_number || "-"}</td> : null}
                    {col("assessor_co") ? <td className="text-truncate" style={{ maxWidth: 130 }} data-hint={r.assessor_company_name || undefined}>{r.assessor_company_name || "-"}</td> : null}
                    {col("assessor_ref") ? <td className="text-nowrap">{r.assessor_ref || "-"}</td> : null}
                    {col("client") ? <td className="text-truncate" style={{ maxWidth: 140 }} data-hint={r.client_name || undefined}>{r.client_name || "-"}</td> : null}
                    {col("address") ? <td className="text-truncate" style={{ maxWidth: 160 }} data-hint={r.street_address || undefined}>{r.street_address || "-"}</td> : null}
                    {col("status") ? <td className="text-nowrap">{r.status_name}</td> : null}
                    {col("accepted") ? <td className="text-nowrap">{r.accepted_on ? formatDate(r.accepted_on) : "-"}</td> : null}
                    {col("hold") ? (
                      <td className="text-nowrap small">
                        {Object.entries(r.on_hold).length === 0
                          ? "-"
                          : Object.entries(r.on_hold).map(([reason, days]) => (
                              <div key={reason}>{reason.replace(/^On Hold /, "")}: {days}</div>
                            ))}
                      </td>
                    ) : null}
                    {col("completed") ? <td className="text-nowrap">{r.completed_on ? formatDate(r.completed_on) : "-"}</td> : null}
                    {col("life") ? (
                      <td className={`text-nowrap fw-semibold ${r.oot ? "text-danger" : r.file_life_days != null ? "text-success" : "text-muted"}`}>
                        {r.file_life_display}
                      </td>
                    ) : null}
                  </tr>
                ))}
                {report.rows.length === 0 ? (
                  <tr><td colSpan={cols.size} className="text-center text-muted">No jobs in this range.</td></tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr className="table-surface fw-semibold">
                  <td colSpan={Math.max(1, cols.size - 1)} className="text-end">Closed File Life</td>
                  <td className="text-nowrap">Avg: {report.summary.avg == null ? "—" : report.summary.avg}</td>
                </tr>
              </tfoot>
            </Table>
          </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default FileLifeReportPage;
